import React, { ReactElement, useCallback, useState } from "react";

import styled from "styled-components";

import { Checkbox } from "../../components/Checkbox";
import { IconButton } from "../../components/IconButton";
import {
  ArrowRightIcon,
  CopyIcon,
  DuplicateIcon,
  EditIcon,
  RecurringCheckmarkIcon,
  TaskListCompleteIcon,
  TrashIcon,
  TreeDotsIcon,
} from "../../components/Icons";
import { List, ListItem, ListSeparator } from "../../components/List";
import { Menu } from "../../components/Menu";
import { Tooltip } from "../../components/Tooltip";

/* Task three-dot options menu — same component as the real TaskItem
   (Menu in "normal" mode = 260px, List/ListItem + TreeDots target), shared by
   the List view and Column view presentations. Spacing is styled-components
   (this Storybook has no runtime Tailwind). Clicks are intentionally no-ops. */
const StyledTaskMenu = styled.div`
  /* no width — fills the 260px Menu bubble */

  .menu-list {
    padding: 8px 0;
  }

  /* text on the left, control (arrow / checkbox) on the right */
  .menu-item {
    justify-content: space-between;
  }

  .menu-item .label {
    min-width: 0;
    padding-right: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* bottom action icons: same horizontal padding as a list item (0 1rem),
     spread evenly */
  .menu-bottom {
    display: flex;
    justify-content: space-between;
    padding: 0 16px;
    margin: 16px 0 12px;
  }
`;

const MENU_PICKER_ITEMS = [
  "Task List",
  "Assignees",
  "Due Date",
  "Label",
  "Estimate",
];

export interface TaskOptionsMenuProps {
  /** Class on the TreeDots trigger (parent controls its hover visibility). */
  targetClassName?: string;
  /** IconButton size of the trigger (omit for the default/regular size). */
  size?: "small" | "big";
}

export const TaskOptionsMenu = ({
  targetClassName,
  size,
}: TaskOptionsMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const triggerClassName =
    [targetClassName, open ? "open" : ""].filter(Boolean).join(" ") ||
    undefined;

  return (
    <Menu
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      position="bottom-end"
      target={
        <IconButton
          type="button"
          variant="text gray"
          size={size}
          className={triggerClassName}
          active={open}
        >
          <TreeDotsIcon />
        </IconButton>
      }
    >
      <StyledTaskMenu>
        <List className="menu-list" tabIndex={0}>
          {MENU_PICKER_ITEMS.map((name) => (
            <ListItem key={name} className="menu-item">
              <div className="label">{name}</div>
              <ArrowRightIcon />
            </ListItem>
          ))}
          <ListSeparator />
          <ListItem className="menu-item">
            <div className="label">Hidden from clients</div>
            <Checkbox
              id="task-menu-hidden-from-clients"
              onChange={() => undefined}
            />
          </ListItem>
          <ListItem className="menu-item">
            <div className="label">High priority</div>
            <Checkbox id="task-menu-high-priority" onChange={() => undefined} />
          </ListItem>
          <ListSeparator />
          <div className="menu-bottom">
            <Tooltip title="Edit">
              <IconButton type="button" variant="text gray">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton type="button" variant="text gray">
                <DuplicateIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create Recurring Task">
              <IconButton type="button" variant="text gray">
                <RecurringCheckmarkIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton type="button" variant="text gray">
                <TrashIcon />
              </IconButton>
            </Tooltip>
          </div>
        </List>
      </StyledTaskMenu>
    </Menu>
  );
};

TaskOptionsMenu.displayName = "TaskOptionsMenu";

export interface TaskListOptionsMenuProps {
  /** Class on the TreeDots trigger (parent controls its hover visibility). */
  targetClassName?: string;
  /** IconButton size of the trigger (omit for the default/regular size). */
  size?: "small" | "big";
}

/* Task-LIST three-dot menu (Menu + List with leading-icon items) — same
   options as the real TaskListOptions. Shared by List & Column views.
   Clicks are intentionally no-ops. */
export const TaskListOptionsMenu = ({
  targetClassName,
  size,
}: TaskListOptionsMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const triggerClassName =
    [targetClassName, open ? "open" : ""].filter(Boolean).join(" ") ||
    undefined;

  return (
    <Menu
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      position="bottom-end"
      target={
        <IconButton
          type="button"
          variant="text gray"
          size={size}
          className={triggerClassName}
          active={open}
        >
          <TreeDotsIcon />
        </IconButton>
      }
    >
      <List style={{ padding: "8px 0" }} tabIndex={-1}>
        <ListItem>
          <EditIcon />
          Rename
        </ListItem>
        <ListItem>
          <DuplicateIcon />
          Duplicate
        </ListItem>
        <ListItem>
          <CopyIcon />
          Move/Copy to Project
        </ListItem>
        <ListItem>
          <TaskListCompleteIcon />
          Complete
        </ListItem>
        <ListSeparator />
        <ListItem>
          <TrashIcon />
          Delete
        </ListItem>
      </List>
    </Menu>
  );
};

TaskListOptionsMenu.displayName = "TaskListOptionsMenu";
